import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import useGoogleMapsLoader from "../../../../Hooks/useGoogleMapsLoader";
import {
  calculateRouteDistanceKm,
  formatCoordinate,
} from "./routeMasterFormUtils";
import RouteMapModalFooter from "./RouteMapModalFooter";

const DEFAULT_CENTER = { lat: 28.613939, lng: 77.209021 };

const mapContainerStyle = { width: "100%", height: "100%" };

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: false,
  clickableIcons: false,
};

const getMarkerIcon = (type) => {
  if (!window.google?.maps) return undefined;

  if (type === "start") {
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: "#2563eb",
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeWeight: 2,
      scale: 12,
      labelOrigin: new window.google.maps.Point(0, 0),
    };
  }

  if (type === "end") {
    return {
      path: window.google.maps.SymbolPath.CIRCLE,
      fillColor: "#dc2626",
      fillOpacity: 1,
      strokeColor: "#ffffff",
      strokeWeight: 2,
      scale: 12,
      labelOrigin: new window.google.maps.Point(0, 0),
    };
  }

  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: "#ffffff",
    fillOpacity: 1,
    strokeColor: "#111827",
    strokeWeight: 1,
    scale: 2.5,
  };
};

const getMarkerLabel = (type) => {
  if (type === "start") {
    return {
      text: "S",
      color: "#ffffff",
      fontSize: "12px",
      fontWeight: "700",
    };
  }

  if (type === "end") {
    return {
      text: "E",
      color: "#ffffff",
      fontSize: "12px",
      fontWeight: "700",
    };
  }

  return undefined;
};

const RouteDrawMapModal = ({ show, onHide, initialPoints = [], onSave }) => {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const mapRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [cursorPosition, setCursorPosition] = useState(null);

  useEffect(() => {
    if (show) {
      setPoints(Array.isArray(initialPoints) ? initialPoints : []);
      setCursorPosition(null);
    }
  }, [show, initialPoints]);

  const mapCenter = useMemo(() => {
    if (points.length) return points[0];
    return DEFAULT_CENTER;
  }, [points]);

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setPoints((previous) => [...previous, { lat, lng }]);
  };

  const handleUndo = () => {
    setPoints((previous) => previous.slice(0, -1));
  };

  const handleSave = () => {
    onSave?.(points);
    onHide?.();
  };

  const handleClose = () => {
    onHide?.();
  };

  const latestPoint = points[points.length - 1] ?? cursorPosition ?? mapCenter;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      aria-labelledby="route-draw-map-title"
    >
      <Modal.Body style={{ padding: 0 }}>
        <div style={{ position: "relative", height: "70vh", minHeight: 480 }}>
          {!isLoaded && !loadError ? (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              Loading map...
            </div>
          ) : null}

          {loadError ? (
            <div className="d-flex align-items-center justify-content-center h-100 text-danger">
              Unable to load map.
            </div>
          ) : null}

          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={13}
              options={mapOptions}
              onClick={handleMapClick}
              onMouseMove={(event) => {
                setCursorPosition({
                  lat: event.latLng.lat(),
                  lng: event.latLng.lng(),
                });
              }}
              onLoad={(map) => {
                mapRef.current = map;
                map.panTo(mapCenter);
              }}
            >
              {points.length > 1 ? (
                <Polyline
                  path={points}
                  options={{
                    strokeColor: "#111827",
                    strokeOpacity: 1,
                    strokeWeight: 3,
                  }}
                />
              ) : null}

              {points.map((point, index) => {
                let markerType = "middle";
                if (index === 0) markerType = "start";
                if (index === points.length - 1 && points.length > 1) {
                  markerType = "end";
                }

                return (
                  <Marker
                    key={`${point.lat}-${point.lng}-${index}`}
                    position={point}
                    icon={getMarkerIcon(markerType)}
                    label={getMarkerLabel(markerType)}
                    title={
                      markerType === "start"
                        ? "Start Point (S)"
                        : markerType === "end"
                          ? "End Point (E)"
                          : `Point ${index + 1}`
                    }
                  />
                );
              })}
            </GoogleMap>
          ) : null}

          <div
            style={{
              position: "absolute",
              left: 16,
              bottom: 16,
              background: "#ffffff",
              border: "1px solid #dbeafe",
              borderRadius: 8,
              padding: "8px 12px",
              fontSize: 13,
              color: "#1e293b",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
              zIndex: 2,
            }}
          >
            <i className="fa-solid fa-location-dot me-2 text-primary" />
            Latitude: {latestPoint.lat.toFixed(4)}, Longitude:{" "}
            {latestPoint.lng.toFixed(4)}
          </div>

          <button
            type="button"
            onClick={handleUndo}
            disabled={!points.length}
            style={{
              position: "absolute",
              right: 16,
              bottom: 16,
              border: "none",
              borderRadius: 8,
              background: "#f97316",
              color: "#ffffff",
              fontWeight: 600,
              padding: "10px 18px",
              zIndex: 2,
              opacity: points.length ? 1 : 0.6,
              cursor: points.length ? "pointer" : "not-allowed",
            }}
          >
            <i className="fa-solid fa-rotate-left me-2" />
            UNDO
          </button>
        </div>

        <div className="p-3 border-top bg-light">
          <div className="d-flex justify-content-between flex-wrap gap-2">
            <div className="text-muted" style={{ fontSize: 14 }}>
              {points.length
                ? `${points.length} point(s) drawn • ${calculateRouteDistanceKm(points) || "0.00"} Km`
                : "Click on the map to draw route coordinates."}
            </div>
            <div className="text-muted" style={{ fontSize: 13 }}>
              Start: {points[0] ? formatCoordinate(points[0]) : "-"} | End:{" "}
              {points.length > 1 ? formatCoordinate(points[points.length - 1]) : "-"}
            </div>
          </div>
        </div>
      </Modal.Body>

      <RouteMapModalFooter onClose={handleClose} onSave={handleSave} />
    </Modal>
  );
};

export default RouteDrawMapModal;
